/**
 * @fileoverview CardSectionHeader — h6 title row with an optional right-
 * anchored action button. Used above the Getting Started, Favorites, and
 * Resources sections on the Control Panel home page.
 *
 * Foundry mapping: `control-panel-app__header__1gh3j8l` containing an
 * `h6.bp6-heading` + right-aligned action.
 */

import { H6 } from "@blueprintjs/core";
import type { ReactElement, ReactNode } from "react";
import { controlPanelApp as cpa } from "./foundry-parts.generated";

interface CardSectionHeaderProps {
  title: string;
  /** Right-anchored action slot (typically a Button). */
  action?: ReactNode;
  className?: string;
}

export function CardSectionHeader({
  title,
  action,
  className,
}: CardSectionHeaderProps): ReactElement {
  return (
    /*
      `header@1gh3j8l` and not a bare `header`: FOUR modules define a `header`
      part, two of them in this very namespace, and the generator refuses the
      unqualified name for exactly that reason. 1wd12z is the Control Panel home
      module — it also owns `search-bar`, `subtitle` and `title-and-description`,
      which are the other elements on this page. Picking a different hash would
      still resolve, and would style this with another module's rules.

      A `div`, not a `<header>`. The capture has a plain div here and the
      element is purely presentational — the heading inside it is what carries
      the structure for a screen reader, and `<header>` scoped inside a card is
      not a landmark, so nothing is lost by matching.
    */
    <div data-testid="card-section-header"
      className={[cpa["header@1gh3j8l"], "bp-card-section-header", className].filter(Boolean).join(" ")}
    >
      <H6>{title}</H6>
      {/* The action sits directly in the header, not in a wrapper — the
          capture has `div.__header__ > h6 + button|a`. */}
      {action}
    </div>
  );
}
